'use client';

import { usePathname } from 'next/navigation';
import React, { useEffect, useRef } from 'react';

import useCookies from '@cineamo/ui/src/hooks/useCookies';
import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import GoogleAnalytics from '@cineamo/ui/src/misc/GoogleAnalytics';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

/**
 * Loads gtag.js for the GA4 property configured for the cinema (cinema app → Website → Google
 * Analytics) once the visitor has consented to analysis cookies, and sends a `page_view` on
 * client-side route changes, since the SPA navigation after the initial load isn't a full page load.
 *
 * Custom events are not sent from here — they reach the same property through the tracker
 * registered in `utils/Providers.tsx`.
 */
export default function GoogleAnalyticsTracker(): React.JSX.Element | null {
    const domainConfiguration = useDomainConfiguration();
    const measurementId = domainConfiguration.websiteConfiguration?.googleAnalyticsMeasurementId?.trim();

    const { analysisCookiesPermissionState } = useCookies();

    // The consent cookie can only be read client-side; `useCookies()` reports its opt-out default
    // during hydration, which would otherwise mismatch a returning visitor's real, previously
    // accepted value. Deferring to post-mount keeps the first client render aligned with the
    // server-rendered output (both null) and only loads gtag.js once the real consent value is
    // known.
    const isMounted = useIsMounted();

    const isEnabled = isMounted && !!measurementId && analysisCookiesPermissionState;

    const pathname = usePathname();
    const isInitialPageviewRef = useRef(true);

    useEffect(() => {
        if (!isEnabled || typeof window === 'undefined' || !window.gtag) {
            return;
        }

        // The `config` call in the loader below already counts the first page view.
        if (isInitialPageviewRef.current) {
            isInitialPageviewRef.current = false;
            return;
        }

        window.gtag('event', 'page_view', {
            page_path: window.location.pathname,
            page_location: window.location.href,
            page_title: document.title
        });
    }, [isEnabled, pathname]);

    if (!isEnabled) {
        return null;
    }

    return (
        <GoogleAnalytics
            trackingId={measurementId}
            consentMode
        />
    );
}
